import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DirectusService } from '../core/services/directus.service';

type GuestFilter = 'all' | 'recent' | 'older';

interface GuestRow {
  id: string;
  initials: string;
  name: string;
  email: string;
  phone: string;
  status: 'Event guest';
  lastActivity: string;
  address: string;
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <section class="people-page">
      <header class="people-header">
        <h2>Guests</h2>
        <p>Guest contacts captured from event registrations.</p>
      </header>

      <section class="table-shell">
        <div class="toolbar">
          <div class="left">
            <select [(ngModel)]="filter">
              <option value="all">All contacts</option>
              <option value="recent">Recent (last 30 days)</option>
              <option value="older">Older contacts</option>
            </select>
            <button type="button" class="linkish">Manage View</button>
          </div>
          <div class="right">
            <button type="button" class="btn-outline">Filter</button>
            <button type="button" class="btn-icon">≋</button>
            <input type="search" [(ngModel)]="searchTerm" placeholder="Search..." />
          </div>
        </div>

        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th class="check"><input type="checkbox" /></th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Contact type</th>
                <th>Last activity</th>
                <th>Address</th>
                <th class="actions"></th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of filteredRows">
                <td class="check"><input type="checkbox" /></td>
                <td class="name-cell">
                  <span class="avatar">{{ row.initials }}</span>
                  <span class="name">{{ row.name }}</span>
                </td>
                <td>{{ row.email }}</td>
                <td>{{ row.phone }}</td>
                <td><span class="status-badge">{{ row.status }}</span></td>
                <td>{{ row.lastActivity }}</td>
                <td>{{ row.address }}</td>
                <td class="actions"><button type="button" class="dots">···</button></td>
              </tr>
              <tr *ngIf="!loading && !error && filteredRows.length === 0">
                <td colspan="8" class="empty">No guest contacts found.</td>
              </tr>
              <tr *ngIf="!loading && error">
                <td colspan="8" class="empty error">{{ error }}</td>
              </tr>
              <tr *ngIf="loading">
                <td colspan="8" class="empty">Loading guest contacts...</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </section>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
      max-width: 1320px;
      box-sizing: border-box;
      margin: 0 auto;
    }

    .people-page {
      display: grid;
      gap: 12px;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    }

    .people-header h2 {
      margin: 0;
      font-size: var(--type-page-title);
      line-height: 1.15;
      letter-spacing: -0.02em;
      font-weight: 700;
      color: var(--app-text);
    }

    .people-header p {
      margin: 0.35rem 0 0;
      color: var(--app-muted);
      font-size: var(--type-body);
    }

    .table-shell {
      border: 1px solid var(--app-border);
      background: var(--app-surface);
      border-radius: 12px;
      overflow: hidden;
    }

    .toolbar {
      padding: 0.8rem 1rem;
      display: flex;
      justify-content: space-between;
      gap: 12px;
      border-bottom: 1px solid var(--app-border);
    }

    .left,
    .right {
      display: flex;
      gap: 10px;
      align-items: center;
    }

    select,
    input[type='search'] {
      border: 1px solid #cbd5e1;
      background: var(--jci-white);
      color: var(--app-text);
      border-radius: 999px;
      padding: 0.4rem 0.7rem;
      font-size: var(--type-body);
    }

    input[type='search'] {
      min-width: 220px;
    }

    .linkish {
      border: 0;
      background: transparent;
      color: var(--jci-blue);
      font-weight: 500;
      cursor: pointer;
    }

    .btn-outline {
      border: 1px solid #b7dff0;
      border-radius: 999px;
      background: #f4fbff;
      color: var(--jci-blue);
      font-weight: 600;
      padding: 0.4rem 0.8rem;
    }

    .btn-icon {
      width: 34px;
      height: 34px;
      border: 1px solid #b7dff0;
      border-radius: 999px;
      background: #f4fbff;
      color: var(--jci-blue);
    }

    .table-wrap {
      overflow-x: auto;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      min-width: 980px;
    }

    thead tr {
      background: #e6f4fb;
    }

    th,
    td {
      padding: 0.62rem 0.65rem;
      border-bottom: 1px solid var(--app-border);
      text-align: left;
      font-size: 0.95rem;
      color: var(--app-text);
      vertical-align: middle;
    }

    th.check,
    td.check {
      width: 34px;
      text-align: center;
    }

    th.actions,
    td.actions {
      width: 56px;
      text-align: right;
    }

    .name-cell {
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 220px;
    }

    .avatar {
      width: 36px;
      height: 36px;
      border-radius: 999px;
      display: inline-grid;
      place-items: center;
      font-size: 0.78rem;
      font-weight: 700;
      color: #fff;
      background: #2d9cdb;
      flex: 0 0 auto;
    }

    .name {
      font-weight: 600;
    }

    .status-badge {
      border: 1px solid #93c5fd;
      color: #1e4f8f;
      background: #eff6ff;
      font-size: 0.78rem;
      font-weight: 700;
      border-radius: 4px;
      padding: 0.14rem 0.4rem;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      white-space: nowrap;
    }

    .dots {
      width: 34px;
      height: 34px;
      border: 1px solid #b7dff0;
      border-radius: 999px;
      background: var(--jci-white);
      color: var(--jci-blue);
    }

    .empty {
      text-align: center;
      color: var(--app-muted);
      padding: 1rem;
    }

    .empty.error {
      color: #dc2626;
    }

    @media (max-width: 1080px) {
      .toolbar {
        flex-direction: column;
        align-items: stretch;
      }
    }
  `
})
export class GuestsComponent implements OnInit {
  loading = true;
  error = '';
  searchTerm = '';
  filter: GuestFilter = 'all';
  rows: GuestRow[] = [];

  constructor(private readonly directus: DirectusService) {}

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  get filteredRows(): GuestRow[] {
    const needle = this.searchTerm.trim().toLowerCase();
    return this.rows.filter((row) => {
      const sourceDate = this.parseDate(row.lastActivity);
      const ageDays = sourceDate ? (Date.now() - sourceDate.getTime()) / (1000 * 60 * 60 * 24) : Infinity;
      const byFilter =
        this.filter === 'all' ? true : this.filter === 'recent' ? ageDays <= 30 : ageDays > 30;
      if (!byFilter) return false;
      if (!needle) return true;
      return [row.name, row.email, row.phone, row.address, row.lastActivity].join(' ').toLowerCase().includes(needle);
    });
  }

  private async load(): Promise<void> {
    this.loading = true;
    try {
      const data = await this.directus.list('guest_contacts', {
        fields: 'id,first_name,last_name,email,phone,whatsapp,last_seen_at,date_created,date_updated',
        sort: '-date_created',
        limit: 500
      });

      this.error = '';
      this.rows = data.map((raw) => {
        const row = raw as Record<string, unknown>;
        const first = String(row['first_name'] ?? '').trim();
        const last = String(row['last_name'] ?? '').trim();
        const name = [first, last].filter(Boolean).join(' ').trim() || 'Guest';
        const activitySource = String(row['last_seen_at'] ?? row['date_updated'] ?? row['date_created'] ?? '').trim();
        return {
          id: String(row['id'] ?? ''),
          initials: this.initials(first, last, name),
          name,
          email: String(row['email'] ?? '').trim() || '—',
          phone: String(row['phone'] ?? row['whatsapp'] ?? '').trim() || '—',
          status: 'Event guest',
          lastActivity: this.dateLabel(activitySource),
          address: '—'
        } as GuestRow;
      });
    } catch (err: unknown) {
      this.error = err instanceof Error ? err.message : 'Failed to load guest contacts.';
    } finally {
      this.loading = false;
    }
  }

  private initials(first: string, last: string, fallback: string): string {
    const a = first.charAt(0);
    const b = last.charAt(0);
    const fromNames = `${a}${b}`.trim();
    if (fromNames) return fromNames.toUpperCase();
    return fallback.slice(0, 2).toUpperCase();
  }

  private dateLabel(value: string): string {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  }

  private parseDate(label: string): Date | null {
    if (label === '—') return null;
    const date = new Date(label);
    return Number.isNaN(date.getTime()) ? null : date;
  }
}
